import { NavLink } from 'react-router-dom';
import {
  IoHomeOutline,
  IoCubeOutline,
  IoMailOutline,
  IoWalletOutline,
  IoPersonOutline,
} from 'react-icons/io5';
import { cn } from '@/components/ui/utils';
import { useInboxUnreadCount } from '@/hooks/useInbox';

/**
 * Mobile bottom tab bar (PEL-817). Visible only on <768px; hidden on md+.
 *
 * Style guide §10:
 *   Active tab: colors.orange icon + label
 *   Inactive tab: colors.textMuted icon + label
 *
 * 5 tabs for mobile (per PEL-817 AC):
 *   Home, Orders, Inbox, Wallet, Profile.
 * PeliPoints lives in the TopBar chip on mobile to respect the 5-tab budget.
 */

type TabItem = {
  to: string;
  label: string;
  Icon: typeof IoHomeOutline;
};

const TABS: TabItem[] = [
  { to: '/app', label: 'Home', Icon: IoHomeOutline },
  { to: '/app/orders', label: 'Orders', Icon: IoCubeOutline },
  { to: '/app/inbox', label: 'Inbox', Icon: IoMailOutline },
  { to: '/app/wallet', label: 'Wallet', Icon: IoWalletOutline },
  { to: '/app/profile', label: 'Profile', Icon: IoPersonOutline },
];

export function BottomTabBar() {
  const { data: unread } = useInboxUnreadCount();
  const unreadCount = unread?.count ?? 0;

  return (
    <nav
      className={cn(
        'md:hidden',
        'fixed bottom-0 inset-x-0 z-40',
        'bg-[var(--surface)] border-t border-[var(--border)]',
        // iOS safe-area padding so tabs clear the home indicator
        'pb-[env(safe-area-inset-bottom)]',
      )}
      aria-label="Primary navigation"
    >
      <ul className="flex items-stretch justify-around">
        {TABS.map(({ to, label, Icon }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={to === '/app'}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center gap-1',
                  'py-2 min-h-[56px]',
                  'text-[11px] font-medium',
                  'transition-colors',
                  isActive
                    ? 'text-[var(--cta)]'
                    : 'text-[var(--text-muted)]',
                )
              }
            >
              <span className="relative">
                <Icon size={22} aria-hidden />
                {label === 'Inbox' && unreadCount > 0 && (
                  <span
                    aria-label={`${unreadCount} unread`}
                    className="absolute -top-1 -right-2 flex min-w-[16px] h-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
              <span>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
